import React, { useRef, useState, useCallback, useEffect } from 'react';
import './index.css';

/* ======================== CONFIG ======================== */
const VIDEO_SRC = '/videos/003.mp4';

/* ====================== TIME HELPERS ===================== */
function useServerClockSkew() {
  const skewRef = useRef(0);

  const fetchServerNow = useCallback(async () => {
    try {
      const res = await fetch('/', { method: 'HEAD', cache: 'no-store' });
      const dateStr = res.headers.get('date');
      if (dateStr) {
        const serverMs = new Date(dateStr).getTime();
        skewRef.current = serverMs - Date.now();
      }
    } catch {
      skewRef.current = 0;
    }
  }, []);

  const serverNow = useCallback(() => Date.now() + skewRef.current, []);

  return { fetchServerNow, serverNow };
}

function bangkokDateStr(utcMs) {
  const dt = new Date(utcMs + 7 * 3600 * 1000);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/* ========================= APP ========================== */
export default function App() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [msg, setMsg] = useState('');

  const { fetchServerNow, serverNow } = useServerClockSkew();

  /* ---------- เริ่มเล่น ---------- */
  const handleStart = useCallback(async () => {
    setMsg('');
    await fetchServerNow();

    const now = serverNow();
    const todayTH = bangkokDateStr(now);

    // ตรวจว่าเล่นไปแล้วในวันนี้หรือยัง
    const playedDate = sessionStorage.getItem('playedDate');
    if (playedDate === todayTH) {
      setMsg('กรุณาเปิดเบราว์เซอร์ใหม่ หรือรอวันถัดไปเพื่อรับชมอีกครั้ง');
      return;
    }

    setPlaying(true);

    // Fullscreen
    const el = document.documentElement;
    (el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen)?.call(el);
  }, [fetchServerNow, serverNow]);

  /* ---------- เมื่อวิดีโอจบ ---------- */
  useEffect(() => {
    if (!playing) return;
    const video = videoRef.current;

    const onEnded = async () => {
      await fetchServerNow();
      const todayTH = bangkokDateStr(serverNow());
      sessionStorage.setItem('playedDate', todayTH);

      // ออก fullscreen
      if (document.fullscreenElement) {
        try { await document.exitFullscreen(); } catch {}
      }

      setPlaying(false);
      setMsg('ขอบคุณครับ');
    };

    video.addEventListener('ended', onEnded);
    return () => video.removeEventListener('ended', onEnded);
  }, [playing, fetchServerNow, serverNow]);

  /* ---------- โหลดวิดีโอเมื่อกดเริ่ม ---------- */
  useEffect(() => {
    if (!playing) return;

    const video = videoRef.current;
    video.src = VIDEO_SRC;
    video.currentTime = 0;
    video.play().catch(console.warn);
  }, [playing]);

  /* ========================= UI ========================== */
  return (
    <div className={`app ${playing ? 'video-mode' : 'landing-mode'}`}>
      {!playing ? (
        <>
          {msg && <p style={{ color:'#ffcc00', fontWeight:600 }}>{msg}</p>}
          {Array.from({ length: 12 }).map((_, i) => <br key={i} />)}
          <button className="start-btn" onClick={handleStart}>
            🎬 เริ่มเล่นวิดีโอ
          </button>
        </>
      ) : (
        <video
          ref={videoRef}
          className="video-player"
          controls={false}
          playsInline
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '100vh',
            objectFit: 'contain',
            background: '#000',
          }}
        />
      )}
    </div>
  );
}
